import { Booking, Resource } from '@/types';
import {
  MatchSignal,
  ResourceMatchResult,
  RecommendationEngineInput,
  RecommendationEngineOutput,
  AvailablePeriod,
  ConflictDetail,
  SignalWeights,
  DEFAULT_SIGNAL_WEIGHTS,
} from '@/types/recommendation';

export const SIGNAL_WEIGHTS: Record<MatchSignal['key'], number> = {
  availability: 0.35,
  utilisation: 0.18,
  role: 0.18,
  location: 0.09,
  skills: 0.15,
  budget: 0.05,
};

// ---------------------------------------------------------------------------
// Role synonym / family map
// Maps canonical role names to a set of equivalent or closely related titles.
// All matching is case-insensitive. This data lives entirely in this product —
// no external dependency required.
// ---------------------------------------------------------------------------
const ROLE_FAMILIES: Record<string, string[]> = {
  'frontend developer':    ['frontend developer', 'front-end developer', 'front end developer', 'ui developer', 'web developer', 'react developer', 'angular developer', 'vue developer', 'javascript developer'],
  'backend developer':     ['backend developer', 'back-end developer', 'back end developer', 'server-side developer', 'api developer', 'python developer', 'java developer', 'node developer', '.net developer'],
  'fullstack developer':   ['fullstack developer', 'full-stack developer', 'full stack developer', 'software engineer', 'software developer'],
  'senior developer':      ['senior developer', 'senior software engineer', 'lead developer', 'principal developer', 'staff engineer'],
  'ui/ux designer':        ['ui/ux designer', 'ux designer', 'ui designer', 'product designer', 'interaction designer', 'user experience designer', 'user interface designer'],
  'project manager':       ['project manager', 'programme manager', 'program manager', 'delivery manager', 'engagement manager', 'scrum master'],
  'business analyst':      ['business analyst', 'ba', 'systems analyst', 'product analyst', 'requirements analyst'],
  'qa engineer':           ['qa engineer', 'quality assurance engineer', 'test engineer', 'software tester', 'automation engineer', 'sdet'],
  'devops engineer':       ['devops engineer', 'site reliability engineer', 'sre', 'platform engineer', 'infrastructure engineer', 'cloud engineer'],
  'data analyst':          ['data analyst', 'data engineer', 'bi analyst', 'business intelligence analyst', 'reporting analyst'],
  'solution architect':    ['solution architect', 'software architect', 'enterprise architect', 'technical architect', 'systems architect'],
};

/** Build a reverse lookup: any alias → canonical family name */
const ALIAS_TO_FAMILY: Map<string, string> = new Map();
for (const [canonical, aliases] of Object.entries(ROLE_FAMILIES)) {
  for (const alias of aliases) {
    ALIAS_TO_FAMILY.set(alias.toLowerCase(), canonical);
  }
}

// ---------------------------------------------------------------------------
// Seniority hierarchy — ordered from most junior (0) to most senior (4)
// Used to compute seniority fit without any external data.
// ---------------------------------------------------------------------------
const SENIORITY_ORDER: string[] = ['intern', 'junior', 'mid-level', 'senior', 'principal', 'lead', 'staff'];

function seniorityIndex(level: string | undefined): number {
  if (!level) return -1;
  return SENIORITY_ORDER.indexOf(level.toLowerCase());
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Convert percentage-based weights (e.g. 35, 20…) to decimal fractions.
 *  Normalises so they always sum to 1.0, guarding against misconfiguration. */
function resolveWeights(w?: SignalWeights): Record<MatchSignal['key'], number> {
  const src = w ?? DEFAULT_SIGNAL_WEIGHTS;
  const total = src.availability + src.utilisation + src.role + src.location + src.skills + src.budget;
  const safe = total > 0 ? total : 100;
  return {
    availability: src.availability / safe,
    utilisation: src.utilisation / safe,
    role: src.role / safe,
    location: src.location / safe,
    skills: src.skills / safe,
    budget: src.budget / safe,
  };
}

function isBookingForResource(booking: Booking, resourceId: string): boolean {
  if (booking.resourceId && String(booking.resourceId) === resourceId) return true;
  if (booking.resourceIds && booking.resourceIds.includes(resourceId)) return true;
  return false;
}

function dateOnlyMs(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** Calendar days between two dates (inclusive of both endpoints) */
function calendarDaysBetween(a: Date, b: Date): number {
  return Math.max(1, Math.round(Math.abs(dateOnlyMs(b) - dateOnlyMs(a)) / 86400000) + 1);
}

// ---------------------------------------------------------------------------
// Signal: Availability
// Uses hour-level granularity so that partial-day bookings (e.g. 2 hours)
// don't mark the entire day as "fully booked".
// ---------------------------------------------------------------------------
const WORKING_HOURS_PER_DAY = 8;

function computeAvailabilitySignal(
  resource: Resource,
  startDate: Date,
  endDate: Date,
  existingBookings: Booking[]
): MatchSignal & { availablePeriods: AvailablePeriod[]; conflictDetails: ConflictDetail | null } {
  // endDate is an inclusive calendar date (midnight of that day).
  // For overlap detection, treat it as exclusive by advancing to next day midnight.
  const exclusiveEnd = new Date(endDate);
  exclusiveEnd.setDate(exclusiveEnd.getDate() + 1);

  const resourceBookings = existingBookings.filter(b =>
    isBookingForResource(b, resource.id) &&
    new Date(b.startTime) < exclusiveEnd &&
    new Date(b.endTime) > startDate
  );

  const startMs = dateOnlyMs(startDate);
  const endMs = dateOnlyMs(endDate);
  const totalDays = Math.max(1, Math.round((endMs - startMs) / 86400000) + 1);
  const totalHours = totalDays * WORKING_HOURS_PER_DAY;

  // Calculate actual booked hours by summing the overlap between each
  // existing booking and the requested period, clamped to period boundaries.
  const periodStartMs = startDate.getTime();
  const periodEndMs = exclusiveEnd.getTime();
  let bookedHours = 0;
  for (const booking of resourceBookings) {
    const bStartMs = new Date(booking.startTime).getTime();
    const bEndMs = new Date(booking.endTime).getTime();
    const overlapStart = Math.max(bStartMs, periodStartMs);
    const overlapEnd = Math.min(bEndMs, periodEndMs);
    if (overlapEnd > overlapStart) {
      bookedHours += (overlapEnd - overlapStart) / 3600000;
    }
  }
  // Cap so overlapping bookings can't exceed 100%
  bookedHours = Math.min(bookedHours, totalHours);

  const freeHours = totalHours - bookedHours;
  const isFullyBooked = freeHours <= 0;
  const score = isFullyBooked ? 0 : Math.round((freeHours / totalHours) * 100);

  let explanation: string;
  if (isFullyBooked) {
    explanation = 'Fully booked for this period';
  } else if (bookedHours === 0) {
    explanation = 'Free for the full booking period';
  } else {
    const freeRounded = Math.round(freeHours * 10) / 10;
    const totalRounded = Math.round(totalHours * 10) / 10;
    explanation = `Partially free — ${freeRounded}h of ${totalRounded}h available`;
  }

  const availablePeriods: AvailablePeriod[] = [];
  if (!isFullyBooked) {
    availablePeriods.push({
      start: startDate.toISOString(),
      end: endDate.toISOString(),
      type: bookedHours === 0 ? 'full' : 'partial',
    });
  }

  let conflictDetails: ConflictDetail | null = null;
  if (isFullyBooked && resourceBookings.length > 0) {
    const conflictBooking = resourceBookings[0];
    const conflictingResourceCount = conflictBooking.resourceIds
      ? conflictBooking.resourceIds.length
      : 1;
    const impactSummary =
      conflictingResourceCount === 1
        ? `Project ${conflictBooking.projectName || 'Unknown'} will be left with no resource`
        : `Project ${conflictBooking.projectName || 'Unknown'} will go from ${conflictingResourceCount} to ${conflictingResourceCount - 1} resources for this period`;

    conflictDetails = {
      conflictingBookingId: String(conflictBooking.id),
      conflictingProjectId: String(conflictBooking.projectId || ''),
      conflictingProjectName: conflictBooking.projectName || 'Unknown',
      allocationPercent: 100,
      overlapStart: conflictBooking.startTime,
      overlapEnd: conflictBooking.endTime,
      impactSummary,
      canMove: true,
      actionTaken: 'none',
    };
  }

  return {
    key: 'availability',
    label: 'Availability',
    score,
    weight: SIGNAL_WEIGHTS.availability,
    explanation,
    isBlocking: isFullyBooked,
    availablePeriods,
    conflictDetails,
  };
}

// ---------------------------------------------------------------------------
// Signal: Utilisation
// Improvement: window now scales with booking duration instead of a fixed
// 30-day window. A 2-day booking uses a ±7 day window; a 3-month booking
// uses a ±45 day window. This prevents misleadingly high scores for
// resources who are free this week but heavily booked across the full period.
// ---------------------------------------------------------------------------
function computeUtilisationSignal(
  resource: Resource,
  startDate: Date,
  endDate: Date,
  existingBookings: Booking[]
): MatchSignal {
  // Scale the assessment window: half the booking duration either side,
  // clamped between 7 and 60 days so short bookings aren't over-penalised
  // and very long bookings don't create an unreasonably huge window.
  const bookingDays = calendarDaysBetween(startDate, endDate);
  const halfWindow = Math.min(60, Math.max(7, Math.round(bookingDays / 2)));

  const windowStart = new Date(startDate);
  windowStart.setDate(windowStart.getDate() - halfWindow);
  const windowEnd = new Date(endDate);
  windowEnd.setDate(windowEnd.getDate() + halfWindow);

  const windowDays = Math.round((dateOnlyMs(windowEnd) - dateOnlyMs(windowStart)) / 86400000) + 1;
  const bookedDays = new Set<number>();
  const windowStartMs = dateOnlyMs(windowStart);

  for (const booking of existingBookings) {
    if (!isBookingForResource(booking, resource.id)) continue;
    const bStart = new Date(booking.startTime);
    const bEnd = new Date(booking.endTime);
    if (bEnd < windowStart || bStart > windowEnd) continue;

    let cur = dateOnlyMs(new Date(Math.max(bStart.getTime(), windowStart.getTime())));
    const capEnd = dateOnlyMs(new Date(Math.min(bEnd.getTime(), windowEnd.getTime())));
    while (cur <= capEnd) {
      bookedDays.add(Math.round((cur - windowStartMs) / 86400000));
      cur += 86400000;
    }
  }

  const utilisationPct = Math.round((bookedDays.size / windowDays) * 100);
  let score: number;
  if (utilisationPct <= 60) score = 100;
  else if (utilisationPct <= 80) score = 60;
  else if (utilisationPct <= 100) score = 30;
  else score = 0;

  return {
    key: 'utilisation',
    label: 'Utilisation',
    score,
    weight: SIGNAL_WEIGHTS.utilisation,
    explanation: `${utilisationPct}% utilisation across the surrounding period`,
    isBlocking: false,
    displayScore: utilisationPct,
  };
}

// ---------------------------------------------------------------------------
// Signal: Role
// Improvement 1 — role family/synonym map: "React Developer" and
//   "Frontend Developer" now resolve to the same family and score 85,
//   rather than the previous hard 0.
// Improvement 2 — seniority fit bonus/penalty: uses Resource.level
//   (already stored in this product) to nudge the score. Over-qualified
//   resources score slightly lower to surface better-fit options first;
//   under-qualified are penalised more strongly.
// ---------------------------------------------------------------------------
function computeRoleSignal(resource: Resource, requiredRole: string | undefined): MatchSignal {
  if (!requiredRole || requiredRole.trim() === '') {
    return {
      key: 'role',
      label: 'Role',
      score: 100,
      weight: SIGNAL_WEIGHTS.role,
      explanation: 'No role requirement specified',
      isBlocking: false,
      displayScore: null,
    };
  }

  const req = requiredRole.trim().toLowerCase();
  const res = (resource.role || '').trim().toLowerCase();

  // ── Step 1: exact string match ───────────────────────────────────────────
  let roleScore: number;
  let roleExplanation: string;

  if (res === req) {
    roleScore = 100;
    roleExplanation = `Exact role match: ${resource.role}`;
  } else {
    // ── Step 2: family / synonym lookup ─────────────────────────────────
    const reqFamily = ALIAS_TO_FAMILY.get(req);
    const resFamily = ALIAS_TO_FAMILY.get(res);

    if (reqFamily && resFamily && reqFamily === resFamily) {
      roleScore = 85;
      roleExplanation = `Equivalent role — same discipline as ${requiredRole}`;
    } else if (res.includes(req) || req.includes(res)) {
      // ── Step 3: substring overlap (e.g. "Senior Developer" ↔ "Developer") ─
      roleScore = 65;
      roleExplanation = 'Closely related role';
    } else if (reqFamily && resFamily) {
      // Both are known roles but different families — neighbouring disciplines
      // (e.g. Frontend Developer vs Backend Developer) score higher than unknown
      roleScore = 25;
      roleExplanation = 'Different discipline within a known role family';
    } else {
      roleScore = 0;
      roleExplanation = `Role mismatch — requires ${requiredRole}, resource is ${resource.role}`;
    }
  }

  // ── Step 4: seniority fit adjustment (uses Resource.level) ──────────────
  // Extract seniority keywords from requiredRole (e.g. "Senior Frontend Developer")
  const seniorityKeywords = ['junior', 'mid-level', 'mid', 'senior', 'principal', 'lead', 'staff'];
  const reqSeniority = seniorityKeywords.find(k => req.includes(k));
  const resSeniority = resource.level?.toLowerCase();

  let seniorityNote = '';
  let seniorityAdjustment = 0;

  if (reqSeniority && resSeniority) {
    const reqIdx = seniorityIndex(reqSeniority === 'mid' ? 'mid-level' : reqSeniority);
    const resIdx = seniorityIndex(resSeniority);

    if (reqIdx >= 0 && resIdx >= 0) {
      const delta = resIdx - reqIdx;
      if (delta === 0) {
        seniorityNote = `, ${resource.level} level — ideal fit`;
        seniorityAdjustment = 0;
      } else if (delta === 1) {
        seniorityNote = `, slightly over-qualified (${resource.level})`;
        seniorityAdjustment = -5;
      } else if (delta >= 2) {
        seniorityNote = `, significantly over-qualified (${resource.level})`;
        seniorityAdjustment = -15;
      } else if (delta === -1) {
        seniorityNote = `, slightly under-qualified (${resource.level})`;
        seniorityAdjustment = -15;
      } else {
        seniorityNote = `, under-qualified (${resource.level})`;
        seniorityAdjustment = -30;
      }
    }
  } else if (!reqSeniority && resSeniority) {
    // No seniority specified in the requirement — all levels are fine
    seniorityNote = ` (${resource.level})`;
  }

  const finalScore = Math.max(0, Math.min(100, roleScore + seniorityAdjustment));

  return {
    key: 'role',
    label: 'Role',
    score: finalScore,
    weight: SIGNAL_WEIGHTS.role,
    explanation: `${roleExplanation}${seniorityNote}`,
    isBlocking: false,
  };
}

// ---------------------------------------------------------------------------
// Signal: Location
// ---------------------------------------------------------------------------
function computeLocationSignal(resource: Resource, location: string | undefined): MatchSignal {
  if (!location || location.trim() === '') {
    return {
      key: 'location',
      label: 'Location',
      score: 100,
      weight: SIGNAL_WEIGHTS.location,
      explanation: 'No location requirement specified',
      isBlocking: false,
      displayScore: null,
    };
  }

  const req = location.trim().toLowerCase();

  if (!resource.location) {
    return {
      key: 'location',
      label: 'Location',
      score: 0,
      weight: SIGNAL_WEIGHTS.location,
      explanation: 'Location not set for this resource',
      isBlocking: false,
    };
  }

  const res = resource.location.trim().toLowerCase();

  // Remote workers can go anywhere
  if (res === 'remote') {
    return {
      key: 'location',
      label: 'Location',
      score: 80,
      weight: SIGNAL_WEIGHTS.location,
      explanation: 'Remote worker — can be assigned to any location',
      isBlocking: false,
    };
  }

  let score: number;
  let explanation: string;

  if (res === req) {
    score = 100;
    explanation = `Location matched: ${resource.location}`;
  } else {
    const reqFirstWord = req.split(/[\s,]+/)[0];
    const resFirstWord = res.split(/[\s,]+/)[0];
    if (reqFirstWord && resFirstWord && reqFirstWord === resFirstWord) {
      score = 50;
      explanation = 'Same broad region';
    } else {
      score = 0;
      explanation = `Location mismatch — requires ${location}, resource is in ${resource.location}`;
    }
  }

  return {
    key: 'location',
    label: 'Location',
    score,
    weight: SIGNAL_WEIGHTS.location,
    explanation,
    isBlocking: false,
  };
}

// ---------------------------------------------------------------------------
// Signal: Skills
// Improvement: uses ResourceSkill.rating (1–5) — already stored in this
// product — to weight each skill match by proficiency. A skill match on a
// resource with rating 5 contributes more than rating 1. Unmatched skills
// are scored 0 for their share. This replaces the previous binary
// matched/unmatched count with a proficiency-weighted coverage score.
//
// NOTE: The booking only stores a flat string[] of required skill names,
// with no per-skill proficiency requirement or required/optional flag.
// If the other product can expose minimum proficiency per required skill or
// mark skills as required vs nice-to-have, this signal can be significantly
// improved further. Please flag this to the product manager.
// ---------------------------------------------------------------------------
function computeSkillsSignal(resource: Resource, requiredSkills: string[]): MatchSignal {
  if (!requiredSkills || requiredSkills.length === 0) {
    return {
      key: 'skills',
      label: 'Skills',
      score: 100,
      weight: SIGNAL_WEIGHTS.skills,
      explanation: 'No skills requirement specified',
      isBlocking: false,
      displayScore: null,
    };
  }

  const resourceSkills = resource.skills || [];

  if (resourceSkills.length === 0) {
    return {
      key: 'skills',
      label: 'Skills',
      score: 0,
      weight: SIGNAL_WEIGHTS.skills,
      explanation: `No skills recorded — 0 of ${requiredSkills.length} required skills matched`,
      isBlocking: false,
    };
  }

  // Build a lookup: skill name (lowercase) → rating (1–5)
  const skillRatingMap = new Map<string, number>(
    resourceSkills.map(s => [s.name.toLowerCase(), s.rating])
  );

  const MAX_RATING = 5;
  let weightedScore = 0;
  let matchedCount = 0;
  const matchedNames: string[] = [];
  const missingNames: string[] = [];

  for (const required of requiredSkills) {
    const rating = skillRatingMap.get(required.toLowerCase());
    if (rating !== undefined) {
      // Contribution: (rating / MAX_RATING) per matched skill
      weightedScore += rating / MAX_RATING;
      matchedCount++;
      matchedNames.push(required);
    } else {
      missingNames.push(required);
    }
  }

  // Normalise to 0–100
  const score = Math.round((weightedScore / requiredSkills.length) * 100);

  let explanation: string;
  if (matchedCount === 0) {
    explanation = `None of the ${requiredSkills.length} required skills matched`;
  } else if (matchedCount === requiredSkills.length) {
    const avgRating = Math.round(
      matchedNames.reduce((sum, n) => sum + (skillRatingMap.get(n.toLowerCase()) ?? 0), 0) / matchedCount
    );
    explanation = `All ${matchedCount} skills matched — avg proficiency ${avgRating}/5`;
  } else {
    const missing = missingNames.length <= 2
      ? missingNames.join(', ')
      : `${missingNames.slice(0, 2).join(', ')} +${missingNames.length - 2} more`;
    explanation = `${matchedCount} of ${requiredSkills.length} skills matched — missing: ${missing}`;
  }

  return {
    key: 'skills',
    label: 'Skills',
    score,
    weight: SIGNAL_WEIGHTS.skills,
    explanation,
    isBlocking: false,
  };
}

// ---------------------------------------------------------------------------
// Signal: Budget
// ---------------------------------------------------------------------------
function computeBudgetSignal(
  startDate: Date,
  endDate: Date,
  requiredRole: string | undefined,
  projectId: string | null | undefined,
  projects: import('@/types').Project[],
  existingBookings: Booking[],
): MatchSignal {
  const noData: MatchSignal = {
    key: 'budget',
    label: 'Budget',
    score: 100,
    weight: SIGNAL_WEIGHTS.budget,
    explanation: 'No role budget configured for this project',
    isBlocking: false,
    displayScore: null,
  };

  if (!projectId || !requiredRole) return noData;

  const project = projects.find(p => String(p.id) === String(projectId));
  if (!project?.roleBudgetHours?.length) return noData;

  const roleEntry = project.roleBudgetHours.find(
    r => r.role.toLowerCase() === requiredRole.toLowerCase()
  );
  if (!roleEntry) return noData;

  // Collect all months the booking spans
  const months: string[] = [];
  const cursor = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
  const endMonth = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
  while (cursor <= endMonth) {
    const key = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`;
    months.push(key);
    cursor.setMonth(cursor.getMonth() + 1);
  }

  const totalBudgeted = months.reduce(
    (sum, m) => sum + (roleEntry.monthlyHours[m] ?? 0),
    0
  );

  if (totalBudgeted === 0) return noData;

  const HOURS_PER_DAY = 8;

  const consumedHours = existingBookings
    .filter(b => {
      if (String(b.projectId) !== String(projectId)) return false;
      if (b.resourceId === 'unallocated') return false;
      const bStart = new Date(b.startTime);
      const bEnd = new Date(b.endTime);
      return bStart <= endDate && bEnd >= startDate;
    })
    .reduce((sum, b) => {
      const bStart = new Date(b.startTime);
      const bEnd = new Date(b.endTime);
      const overlapStart = bStart < startDate ? startDate : bStart;
      const overlapEnd = bEnd > endDate ? endDate : bEnd;
      const days = Math.max(0, Math.ceil((overlapEnd.getTime() - overlapStart.getTime()) / (1000 * 60 * 60 * 24)));
      return sum + days * HOURS_PER_DAY;
    }, 0);

  const bookingDays = calendarDaysBetween(startDate, endDate);
  const bookingHours = bookingDays * HOURS_PER_DAY;
  const remainingHours = totalBudgeted - consumedHours;
  const afterBookingHours = remainingHours - bookingHours;

  let score: number;
  let explanation: string;

  if (remainingHours <= 0) {
    score = 0;
    explanation = `Role budget exhausted — ${totalBudgeted}h budgeted, ${consumedHours}h consumed`;
  } else if (afterBookingHours < 0) {
    const overBy = Math.abs(afterBookingHours);
    score = Math.max(0, Math.round((remainingHours / bookingHours) * 100));
    explanation = `Booking would exceed budget by ${overBy}h — ${remainingHours}h remaining of ${totalBudgeted}h`;
  } else {
    const headroomPercent = afterBookingHours / totalBudgeted;
    score = Math.min(100, Math.round(50 + headroomPercent * 50));
    explanation = `${afterBookingHours}h remaining after booking — ${totalBudgeted}h budgeted for ${requiredRole}`;
  }

  return {
    key: 'budget',
    label: 'Budget',
    score,
    weight: SIGNAL_WEIGHTS.budget,
    explanation,
    isBlocking: score === 0,
  };
}

// ---------------------------------------------------------------------------
// Main engine
// ---------------------------------------------------------------------------
export function computeRecommendations(
  input: RecommendationEngineInput
): RecommendationEngineOutput {
  const {
    startDate,
    endDate,
    requiredRole,
    location,
    requiredSkills,
    projectId,
    projects,
    allResources,
    existingBookings,
    signalWeights,
  } = input;

  const weights = resolveWeights(signalWeights);

  if (!startDate || !endDate) {
    return { cleanMatches: [], conflictMatches: [], computedAt: Date.now() };
  }

  const cleanMatches: ResourceMatchResult[] = [];
  const conflictMatches: ResourceMatchResult[] = [];

  const scorableResources = allResources.filter(r => r.id !== 'unallocated');

  for (const resource of scorableResources) {
    const availabilityResult = computeAvailabilitySignal(resource, startDate, endDate, existingBookings);
    const utilisationSignal  = computeUtilisationSignal(resource, startDate, endDate, existingBookings);
    const roleSignal         = computeRoleSignal(resource, requiredRole);
    const locationSignal     = computeLocationSignal(resource, location);
    const skillsSignal       = computeSkillsSignal(resource, requiredSkills || []);
    const budgetSignal       = computeBudgetSignal(startDate, endDate, requiredRole, projectId, projects, existingBookings);

    const signals: MatchSignal[] = [
      { key: availabilityResult.key, label: availabilityResult.label, score: availabilityResult.score, weight: weights.availability, explanation: availabilityResult.explanation, isBlocking: availabilityResult.isBlocking },
      { ...utilisationSignal,  weight: weights.utilisation },
      { ...roleSignal,         weight: weights.role },
      { ...locationSignal,     weight: weights.location },
      { ...skillsSignal,       weight: weights.skills },
      { ...budgetSignal,       weight: weights.budget },
    ];

    const overallScore = Math.round(
      signals.reduce((acc, s) => acc + s.score * s.weight, 0)
    );

    const tier: ResourceMatchResult['tier'] =
      overallScore >= 80 ? 'high' : overallScore >= 50 ? 'medium' : 'low';

    const availabilityStatus: ResourceMatchResult['availabilityStatus'] =
      availabilityResult.isBlocking
        ? 'conflict'
        : availabilityResult.availablePeriods.some(p => p.type === 'partial')
        ? 'partial'
        : 'free';

    const result: ResourceMatchResult = {
      resource,
      overallScore,
      tier,
      signals,
      availabilityStatus,
      availablePeriods: availabilityResult.availablePeriods,
      conflictDetails: availabilityResult.conflictDetails,
    };

    if (availabilityResult.isBlocking) {
      conflictMatches.push(result);
    } else {
      cleanMatches.push(result);
    }
  }

  cleanMatches.sort((a, b) => b.overallScore - a.overallScore);
  conflictMatches.sort((a, b) => b.overallScore - a.overallScore);

  return {
    cleanMatches,
    conflictMatches,
    computedAt: Date.now(),
  };
}
